import requester from '@/lib/axios';
import BetaModal from '@/components/admin/courses/BetaModal';
import PublishEditModal from '@/components/admin/courses/PublishEditModal';
import ListLessonsFactory from '@/components/admin/lessons/factory/ListLessonsFactory';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import CourseTitle from '@/components/ui/custom/CourseTitle';
import Skeleton from '@/components/ui/custom/Skeleton';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ModalBank, {
  ModalBankButton,
} from '@/components/ui/custom/modals/ModalBank/ModalBank';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import ModalEmptyActivity from '@/components/ui/custom/modals/ModalEmptyActivity';
import CourseStatus from '@/components/_deprecated/courses/CourseStatus';
import LegalInformationCourseCollapse from '@/components/_deprecated/courses/LegalInformationCourseCollapse';
import Layout from '@/components/template/Layout';
import useCourseEditing from '@/data/hook/useCourseEditing';
import { deleteActivity } from '@/data/services/_deprecated/activityServices';
import {
  createCourseLesson,
  deleteLesson,
  LessonDTO,
  pushLesson,
} from '@/data/services/lessonServices';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import {
  EmptyActivity,
  EmptyActivityWithLesson,
} from '@/models/_deprecated/Activity';
import CoursePath from '@/models/CoursePath';
import { ApiError } from '@/models/Errors';
import Lesson from '@/models/Lesson';
import RouteParams from '@/models/RouteParams';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { ROUTES } from '@/utils/routes';
import {
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorPage from '../notFound/ErrorPage';
import useLessons from './ManageCourseEditing.hook';
import NoAdminScreenForMobile from './NoAdminScreenForMobile';
import CancelChangesModal from './lessons/CancelChangesModal';
import VersioningHeader from './lessons/VersioningHeader';

interface ManageCourseEditingProps {
  page: 'edit' | 'version' | 'draft';
}

function ManageCourseEditing({ page }: ManageCourseEditingProps) {
  type modalOpenType =
    | 'cancel'
    | 'exclude'
    | 'publish'
    | 'beta'
    | 'none'
    | 'activity'
    | 'bank';

  const { slugCourseName = '' } = useParams<RouteParams>();
  const [selectedLesson, setSelectedLesson] = useState<Lesson>();
  const [modalOpen, setModalOpen] = useState<modalOpenType>('none');
  const [isRequestCreate, setIsRequestCreate] = useState(false);
  const [isRequestingCancel, setIsRequestingCancel] = useState(false);
  const navigate = useNavigate();
  const {
    setCourseStatus,
    handleLessonTab,
    invalidateCoursePaths,
    invalidateCoursePath,
  } = useCourseEditing();
  const { t } = useTranslation('translation', {
    keyPrefix: 'adminPage.lesson',
  });
  const { t: tErrors } = useTranslation('translation', { keyPrefix: 'erros' });

  const [emptyActivities, setEmptyActivities] = useState<
    EmptyActivityWithLesson[]
  >([]);

  const [isLoadingDeleteActivities, setIsLoadingDeleteActivities] =
    useState<boolean>(false);

  const {
    data: course,
    isLoading: isLoadingCourse,
    error,
  } = useQuery({
    ...coursePathsQueryKeys.get(slugCourseName ?? ''),
  });

  useEffect(() => {
    if (course) {
      setCourseStatus(course.status);
    }
  }, [course, setCourseStatus]);

  const {
    lessons,
    lessonBlocksWithLessons,
    updateLessons,
    invalidateBlock,
    isLoading: isLoadingLessons,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useLessons(slugCourseName);

  const versioningLessons = lessons?.filter(
    lesson => lesson.status === 'VERSIONING',
  );

  const checkEmptyActivities = async (versioningLessons?: Lesson[]) => {
    const { data: emptyActivities } = await requester().get<EmptyActivity[]>(
      `courses/${slugCourseName}/check-activities/`,
    );
    const hasEmptyActivities = !!emptyActivities.length;

    if (hasEmptyActivities) {
      const emptyActivitiesAux: EmptyActivityWithLesson[] = [];
      versioningLessons?.forEach(versioningLesson => {
        const filterEmptyActivity = emptyActivities.filter(({ lesson }) =>
          lesson.includes(versioningLesson.id),
        );
        if (filterEmptyActivity && filterEmptyActivity.length) {
          filterEmptyActivity.map(activity =>
            emptyActivitiesAux.push({
              ...activity,
              lessonInfo: versioningLesson,
            }),
          );
        }
      });
      setEmptyActivities(emptyActivitiesAux);
    }
    setModalOpen(hasEmptyActivities ? 'activity' : 'beta');
  };

  const deleteActivities = async (emptyActivities: EmptyActivity[]) => {
    setIsLoadingDeleteActivities(true);
    const [request] = await Promise.allSettled(
      emptyActivities.map(emptyActivity =>
        deleteActivity({
          activityId: emptyActivity.id,
          lessonId: emptyActivity.lesson[0],
        }),
      ),
    );
    if (request.status === 'rejected') {
      alert.error(tErrors('serverError'));
    } else {
      setIsLoadingDeleteActivities(false);
      setModalOpen('none');
    }
  };

  async function updateCourseQueries() {
    if (!slugCourseName) return;

    await invalidateCoursePath(slugCourseName);

    await updateLessons?.();

    if (lessonBlocksWithLessons.length > 0) {
      await Promise.all(
        lessonBlocksWithLessons.map(block => invalidateBlock(block.id)),
      );
    }
  }

  async function courseToBeta() {
    try {
      await requester().patch(`courses/${slugCourseName}/`, {
        status: VersioningStatusEnum.BETA,
      });
      alert.success(t('alert.saved'));

      await updateCourseQueries();

      navigate(`/courses/${slugCourseName}`);
    } catch (error) {
      alert.error(getErrorMessage(error));
    }
  }

  function formatCourseName(coursePath?: CoursePath) {
    return coursePath
      ? `${coursePath.course.name} v${coursePath.version.toFixed(1)}`
      : '';
  }

  const { mutateAsync: create } = useMutation(createCourseLesson, {
    async onSuccess() {
      alert.success(t('successCreateMessage'));
      await updateLessons();
    },
    onSettled() {
      setIsRequestCreate(false);
    },
    onError(error) {
      const apiError = new ApiError(error as any);
      if (apiError.fields.includes('abbreviation')) {
        const fieldMessage = apiError.getMessageByField('abbreviation');
        fieldMessage && alert.warning(fieldMessage);
      } else {
        alert.error(apiError.getErrorMessage());
      }
    },
  });

  const createNewLesson = async () => {
    if (!course) return;

    const body: Partial<LessonDTO> = {
      name: 'name',
      description: 'description',
      motivation: { message: 'Boa!' },
    };

    setIsRequestCreate(true);
    await create({ slugCourseName: course.slug, body });
  };
  const closeModal = () => setModalOpen('none');

  async function cancelVersion() {
    try {
      setIsRequestingCancel(true);
      await requester().delete(`courses/${course?.slug}/`);
      alert.success(t('modal.cancel.success'));
      await invalidateCoursePaths();
      navigate('../courses');
    } catch (error) {
      alert.error(getErrorMessage(error));
    } finally {
      setIsRequestingCancel(false);
    }
  }

  function renderModal(selectedLesson: Lesson) {
    setSelectedLesson(selectedLesson);
    setModalOpen('exclude');
  }

  async function deleteCurrentLesson(lesson: Lesson) {
    if (slugCourseName) {
      await deleteLesson(lesson.id, slugCourseName);
    }
  }

  const { isPending: isRequestingDelete, mutate: onDeleteLesson } = useMutation(
    deleteCurrentLesson,
    {
      async onSettled() {
        await updateLessons();
      },
      onSuccess() {
        alert.success(t('successDeleteMessage'));
        setModalOpen('none');
        handleLessonTab({
          activeLessonOrder: null,
          activeTab: 'details',
          previousActiveLessonOrder: null,
        });
      },
      onError(error) {
        alert.error(getErrorMessage(error));
      },
    },
  );

  const onUseLesson = async (lesson: Lesson) => {
    if (!slugCourseName) throw new Error('slugCourseName is undefined');
    try {
      await pushLesson(slugCourseName, {
        ...lesson,
        order: undefined,
      });
      closeModal();
      updateLessons();
    } catch (error) {
      alert.error(getErrorMessage(error));
    }
  };

  const isLoadingLessonList = isLoadingCourse || isLoadingLessons;
  if (error) {
    return <ErrorPage error={error} />;
  }
  return (
    <Layout>
      <div className="md:hidden">
        <NoAdminScreenForMobile />
      </div>

      <div className="hidden md:block">
        <VersioningHeader course={course} page={page} />

        <div className="flex justify-end items-center gap-2.5 mb-6">
          {course && !isLoadingCourse && (
            <CourseStatus status={course.status} version={course.version} />
          )}
          <ConditionalRenderer condition={course && course.version > 1.0}>
            <MainButton
              disabled={isLoadingLessons}
              onClick={() => setModalOpen('cancel')}
              dataTestId={'discardButton'}
              text={t('discard')}
              color="neutral"
              icon={<TrashIcon />}
            />
          </ConditionalRenderer>
          {page === 'draft' || page === 'version' ? (
            <MainButton
              disabled={isLoadingLessons}
              text={t('finish')}
              dataTestId={'finishButton'}
              color="secondary"
              onClick={() => checkEmptyActivities(versioningLessons)}
              icon={<ArrowUpTrayIcon />}
            />
          ) : (
            <MainButton
              text={t('publish')}
              dataTestId={'publishButton'}
              color="secondary"
              onClick={() => setModalOpen('publish')}
              icon={<ArrowUpTrayIcon />}
            />
          )}
          <MainButton
            disabled={isLoadingLessons}
            text={t('see')}
            dataTestId={'seeButton'}
            href={`/courses/${course?.slug}`}
            color="primary"
            icon={<EyeIcon />}
          />
        </div>
        <div className="flex justify-between flex-col gap-5">
          <ConditionalRenderer
            condition={!isLoadingCourse}
            fallback={
              <Skeleton className="bg-neutral/50 w-full h-[60px] rounded-lg" />
            }
          >
            <CourseTitle
              title={formatCourseName(course)}
              backUrl="/courses"
              hasBackButton
            />
          </ConditionalRenderer>

          <ConditionalRenderer condition={course}>
            {course && (
              <ListLessonsFactory
                lessonBlocksWithLessons={lessonBlocksWithLessons}
                lessons={lessons}
                course={course}
                updateLessons={updateLessons}
                deleteLesson={renderModal}
                invalidateBlock={invalidateBlock}
                isLoadingLessonList={isLoadingLessonList}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onReachEnd={fetchNextPage}
              />
            )}
          </ConditionalRenderer>

          <LegalInformationCourseCollapse course={course} />

          <ConditionalRenderer condition={emptyActivities.length}>
            <ModalEmptyActivity
              isRequesting={isLoadingDeleteActivities}
              isVisible={modalOpen === 'activity'}
              emptyActivities={emptyActivities}
              onClickConfirm={async () => {
                setIsLoadingDeleteActivities(true);
                await deleteActivities(emptyActivities);
                await courseToBeta();
              }}
              onClickCancel={closeModal}
            />
          </ConditionalRenderer>

          <ConditionalRenderer condition={selectedLesson}>
            {selectedLesson && (
              <ModalDisable
                isRequesting={isRequestingDelete}
                modalType="delete"
                visible={modalOpen === 'exclude'}
                selectedObjectName={selectedLesson.name}
                translationString={'modalDisableLesson'}
                onClickConfirm={() => onDeleteLesson(selectedLesson)}
                onClickCancel={closeModal}
              />
            )}
          </ConditionalRenderer>

          <ConditionalRenderer condition={course && !isLoadingCourse}>
            {course && !isLoadingCourse && (
              <React.Fragment>
                <CancelChangesModal
                  isLoading={isRequestingCancel}
                  closeModal={closeModal}
                  itemName={formatCourseName(course)}
                  onConfirm={cancelVersion}
                  visible={modalOpen === 'cancel'}
                />

                <BetaModal
                  onClose={closeModal}
                  slug={course.slug}
                  visible={modalOpen === 'beta'}
                />
                <PublishEditModal
                  onClose={closeModal}
                  slug={course.slug}
                  visible={modalOpen === 'publish'}
                />
                <ModalBank
                  visible={modalOpen === 'bank'}
                  onClose={closeModal}
                  initial="lessons"
                  onUseLesson={onUseLesson}
                />
              </React.Fragment>
            )}
          </ConditionalRenderer>
          <ConditionalRenderer condition={page !== 'edit'}>
            <ConditionalRenderer
              condition={!lessonBlocksWithLessons.length && !!course}
            >
              <div className="flex gap-2">
                <MainButton
                  dataTestId="newLesson"
                  onClick={createNewLesson}
                  loading={isRequestCreate}
                  icon={<PlusIcon className="w-4 h-4" />}
                  text={t('newLesson')}
                  className="w-fit"
                />
                <ModalBankButton
                  onClick={() => setModalOpen('bank')}
                  mode="lessons"
                />
              </div>
            </ConditionalRenderer>
            <ConditionalRenderer condition={lessonBlocksWithLessons.length}>
              <MainButton
                dataTestId="editLessons"
                onClick={() =>
                  navigate(ROUTES.COURSES.STUDIO.STEP(slugCourseName, 'matrix'))
                }
                icon={<PencilIcon className="w-4 h-4" />}
                text={t('editLessons')}
                className="w-fit"
              />
            </ConditionalRenderer>
          </ConditionalRenderer>
        </div>
      </div>
    </Layout>
  );
}

export default ManageCourseEditing;
